import type { Apartment } from '../../api.apartments';

/**
 * The two automatic-sending controls of an apartment (#206): a cap on foreign guests filed per
 * reservation, and a 0-3 day delay after check-in. Both sit behind "Advanced settings" and default to
 * "send every guest, on the check-in day" - most hosts never need either.
 *
 * The cap is `null` for "send all guests" (the switch is on). With the switch off it is a number the
 * host typed, at most the apartment's capacity. The server's own range is 1-50 and it refuses
 * anything outside with a 400 and never clamps, so the page checks first and says what is wrong
 * instead of letting a save fail.
 */

export const AUTO_SEND_MAX_GUESTS_MIN = 1;
/** The server's upper bound, whatever the capacity. */
export const AUTO_SEND_MAX_GUESTS_MAX = 50;
export const AUTO_SEND_DELAY_MAX_DAYS = 3;

export type AutoSendField = 'ubyportAutoSendMaxGuests' | 'ubyportAutoSendDelayDays';

type AutoSendValues = Pick<Apartment, 'autoSendToUbyport' | 'capacity' | AutoSendField>;

/** The largest cap the host may type: the apartment's capacity, within the server's range. */
export const maxGuestsLimit = (capacity: number | null | undefined): number =>
  capacity && capacity >= AUTO_SEND_MAX_GUESTS_MIN
    ? Math.min(Math.floor(capacity), AUTO_SEND_MAX_GUESTS_MAX)
    : AUTO_SEND_MAX_GUESTS_MAX;

/**
 * The cap the page shows for a saved one. A cap at or above the capacity limits nobody, so it is shown
 * (and saved next time) as "send all" instead of as a number larger than the flat can hold - e.g. a
 * cap typed while the capacity was higher.
 */
export const loadedMaxGuests = (v: number | null | undefined, capacity: number | null | undefined): number | null =>
  v === null || v === undefined || v >= maxGuestsLimit(capacity) ? null : v;

/** `null`/`undefined` is valid: send all guests. A number must be a whole 1..limit. */
export const isValidMaxGuests = (v: number | null | undefined, limit: number = AUTO_SEND_MAX_GUESTS_MAX): boolean =>
  v === null || v === undefined ||
  (Number.isInteger(v) && v >= AUTO_SEND_MAX_GUESTS_MIN && v <= limit);

export const isValidDelayDays = (v: number | null | undefined): boolean =>
  v === null || v === undefined || (Number.isInteger(v) && v >= 0 && v <= AUTO_SEND_DELAY_MAX_DAYS);

/**
 * What the number box holds -> what the form stores, while "send all" is off. Only digits are kept,
 * so a minus sign, a decimal point or letters cannot get in. An empty box is `NaN` - invalid, and
 * still "a cap", so emptying the box never flips the switch back to "send all".
 */
export const parseMaxGuestsInput = (raw: string): number => {
  const digits = raw.replace(/\D/g, '').slice(0, 3);
  return digits === '' ? NaN : Number(digits);
};

/** The text the box shows for a stored cap: empty for a box the host cleared. */
export const maxGuestsInputValue = (v: number | null | undefined): string =>
  v === null || v === undefined || Number.isNaN(v) ? '' : String(v);

/**
 * Invalid fields. Only checked while auto-send is on: the controls are not shown otherwise, so an
 * error there would be one the host cannot see or fix.
 */
export function autoSendErrors(a: AutoSendValues): AutoSendField[] {
  if (!a.autoSendToUbyport) return [];
  const out: AutoSendField[] = [];
  if (!isValidMaxGuests(a.ubyportAutoSendMaxGuests, maxGuestsLimit(a.capacity))) out.push('ubyportAutoSendMaxGuests');
  if (!isValidDelayDays(a.ubyportAutoSendDelayDays)) out.push('ubyportAutoSendDelayDays');
  return out;
}

/**
 * With auto-send off the controls are hidden, so a half-typed invalid value left in them must not
 * make the save fail: such a field is sent as last saved instead. Checked against the server's range
 * only - what the server accepts is what matters for a field the host cannot see.
 */
export function withSendableAutoSend<T extends Apartment>(payload: T, saved: Apartment | null): T {
  const out = { ...payload };
  if (!isValidMaxGuests(out.ubyportAutoSendMaxGuests)) out.ubyportAutoSendMaxGuests = saved?.ubyportAutoSendMaxGuests ?? null;
  if (!isValidDelayDays(out.ubyportAutoSendDelayDays)) out.ubyportAutoSendDelayDays = saved?.ubyportAutoSendDelayDays ?? 0;
  return out;
}

/**
 * What the cap and delay become when auto-send is switched on: what the host last saved, so a
 * deliberate "2 guests, 2 days" survives an off/on. Only values that mean something are kept - a cap
 * that limits nobody (at or above the capacity) or a half-typed one becomes "send all", an
 * out-of-range delay becomes the check-in day.
 */
export function autoSendValuesOnSwitchOn(f: AutoSendValues): Pick<Apartment, AutoSendField> {
  const cap = f.ubyportAutoSendMaxGuests;
  const keepCap = typeof cap === 'number' && !Number.isNaN(cap) && isValidMaxGuests(cap, maxGuestsLimit(f.capacity));
  const delay = f.ubyportAutoSendDelayDays;
  const keepDelay = typeof delay === 'number' && isValidDelayDays(delay);
  return {
    ubyportAutoSendMaxGuests: keepCap ? loadedMaxGuests(cap, f.capacity) : null,
    ubyportAutoSendDelayDays: keepDelay ? delay : 0,
  };
}

/** True for the recommended values - every guest, on the check-in day - which need no explaining. */
export const isAutoSendDefault = (v: Pick<Apartment, AutoSendField>): boolean =>
  (v.ubyportAutoSendMaxGuests === null || v.ubyportAutoSendMaxGuests === undefined) && !v.ubyportAutoSendDelayDays;

/**
 * The page shows a stored cap at or above the capacity as "send all" (see loadedMaxGuests), but that
 * is only a display: a save that did not touch the cap must send what the server holds, because for an
 * over-capacity booking that number decides how many guests are filed. `saved` is the form as loaded
 * or last saved; a cap still equal to it has not been touched by the host.
 */
export function withStoredMaxGuests<T extends Apartment>(
  payload: T, saved: Apartment | null, stored: number | null | undefined,
): T {
  if (!saved || payload.ubyportAutoSendMaxGuests !== saved.ubyportAutoSendMaxGuests) return payload;
  return { ...payload, ubyportAutoSendMaxGuests: stored ?? null };
}
